import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { MdClose } from 'react-icons/md';
import { useTranslation } from 'react-i18next';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** Preset max-width sizes – defaults to md */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Optional footer node rendered below the body scroll area */
  footer?: React.ReactNode;
  /** Hide default close button (caller might supply its own) */
  hideCloseButton?: boolean;
  children: React.ReactNode;
  className?: string;
}

const SIZE_MAP: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  footer,
  hideCloseButton = false,
  className = '',
}) => {
  const { t } = useTranslation();

  // Lock scroll when modal is open and listen for Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4 sm:p-6 animate-fade-in"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        className={`bg-white rounded-lg shadow-lg ${SIZE_MAP[size]} w-full max-h-[95vh] flex flex-col overflow-hidden ${className}`}
      >
        {/* Header */}
        {(title || !hideCloseButton) && (
          <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
            {title && (
              <h2 id="modal-title" className="text-lg font-semibold text-gray-900 truncate">
                {title}
              </h2>
            )}
            {!hideCloseButton && (
              <button
                onClick={onClose}
                aria-label={t("app.close")}
                className="text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
              >
                <MdClose className="w-6 h-6" />
              </button>
            )}
          </div>
        )}

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">{children}</div>

        {/* Optional Footer */}
        {footer && <div className="flex-shrink-0 border-t border-gray-200 p-4 sm:p-6">{footer}</div>}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default Modal;
